import { useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { api, fmtDate, type Member } from '../api';
import { byName } from '../memberSearch';
import { useData } from '../App';
import { DeleteButton, Empty, Icon, IconButton } from '../ui';

type Filter = 'allir' | 'felagar' | 'gestir';

type SortKey = 'name' | 'reg' | 'attendance';
type Sort = { key: SortKey; dir: 'asc' | 'desc' };

const DEFAULT_DIR: Record<SortKey, Sort['dir']> = {
  name: 'asc',
  reg: 'desc',
  attendance: 'desc',
};

/* The table head is hidden on a phone, so the same orders are offered
   as a plain control there. Labels, not column names — "flestar
   mætingar" says what you get; "Mætingar ▼" does not. */
const SORT_CHOICES: [string, string][] = [
  ['name:asc', 'Nafn (A–Ö)'],
  ['name:desc', 'Nafn (Ö–A)'],
  ['attendance:desc', 'Flestar mætingar'],
  ['attendance:asc', 'Fæstar mætingar'],
  ['reg:desc', 'Félagar fyrst'],
  ['reg:asc', 'Gestir fyrst'],
];

/* Defined out here, not inside Felagar: a component declared during
   render is a new type on every render, so React tears the header down
   and rebuilds it — which drops keyboard focus the moment you sort. */
function Th({
  k,
  sort,
  onSort,
  children,
}: {
  k: SortKey;
  sort: Sort;
  onSort: (k: SortKey) => void;
  children: ReactNode;
}) {
  const on = sort.key === k;
  return (
    <th aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className={`sortb${on ? ' on' : ''}`} onClick={() => onSort(k)}>
        {children}
        <span className={`sort-caret${on && sort.dir === 'asc' ? ' up' : ''}`} aria-hidden="true">
          {on && <Icon.chev />}
        </span>
      </button>
    </th>
  );
}

export default function Felagar() {
  const { members, refresh } = useData();

  const [filter, setFilter] = useState<Filter>('allir');
  const [sort, setSort] = useState<Sort>({ key: 'name', dir: 'asc' });

  /* First click on a column uses the order that column is usually
     wanted in — names from A, counts from the top. */
  function sortBy(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: DEFAULT_DIR[key] }));
  }


  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [contact, setContact] = useState('');
  const [newRegistered, setNewRegistered] = useState(false);
  const [busy, setBusy] = useState(false);

  /* inline row editing */
  const [editId, setEditId] = useState<number | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftNote, setDraftNote] = useState('');
  const [draftContact, setDraftContact] = useState('');
  const [draftReg, setDraftReg] = useState(false);
  const [saving, setSaving] = useState(false);

  const registered = members.filter((m) => m.is_registered);
  const guests = members.filter((m) => !m.is_registered);
  /* The API hands these over in attendance order, which ranks the
     regulars but gives the eye nothing to search by. Name is the
     default because this is the page you come to looking for one
     particular person; the other orders are a click away. */
  const compare = (a: Member, b: Member): number => {
    const r =
      sort.key === 'attendance'
        ? a.attendance - b.attendance
        : sort.key === 'reg'
          ? (a.is_registered ? 1 : 0) - (b.is_registered ? 1 : 0)
          : byName(a, b);
    // Ties fall back to the name, so equal rows never shuffle about.
    return (sort.dir === 'desc' ? -r : r) || byName(a, b);
  };

  const shown = [
    ...(filter === 'felagar' ? registered : filter === 'gestir' ? guests : members),
  ].sort(compare);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.addMember({
        name: name.trim(),
        contact: contact.trim(),
        note: note.trim(),
        is_registered: newRegistered,
      });
      setName('');
      setNote('');
      setContact('');
      setNewRegistered(false);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  function beginEdit(m: (typeof members)[number]) {
    setEditId(m.id);
    setDraftName(m.name);
    setDraftNote(m.note);
    setDraftContact(m.contact ?? '');
    setDraftReg(!!m.is_registered);
  }

  function cancelEdit() {
    setEditId(null);
    setDraftName('');
    setDraftNote('');
    setDraftContact('');
    setDraftReg(false);
  }

  async function saveEdit() {
    if (editId === null || !draftName.trim()) return;
    setSaving(true);
    try {
      await api.updateMember(editId, {
        name: draftName.trim(),
        contact: draftContact.trim(),
        note: draftNote.trim(),
        is_registered: draftReg,
      });
      cancelEdit();
      await refresh();
    } finally {
      setSaving(false);
    }
  }

  /** One-click promote/demote without entering edit mode. */
  async function toggleMembership(id: number, next: boolean) {
    await api.updateMember(id, { is_registered: next });
    await refresh();
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  }

  async function remove(id: number, who: string) {
    if (!window.confirm(`Eyða ${who}? Mætingarsaga viðkomandi hverfur líka.`)) return;
    await api.delMember(id);
    if (editId === id) cancelEdit();
    await refresh();
  }

  return (
    <div className="view">
      <div className="g-two">
        <section className="card col-8">
          <div className="card-h">
            <h2>Spilarar</h2>
            <span className="lbl">
              {registered.length} félagar · {guests.length} gestir
            </span>
          </div>

          <div className="filters">
            {(
              [
                ['allir', 'Allir', members.length],
                ['felagar', 'Skráðir félagar', registered.length],
                ['gestir', 'Gestir', guests.length],
              ] as const
            ).map(([key, label, n]) => (
              <button
                key={key}
                type="button"
                className={`filt${filter === key ? ' on' : ''}`}
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
              >
                {label}
                <span className="n">{n}</span>
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <Empty
              title={
                filter === 'felagar'
                  ? 'Enginn skráður félagi enn'
                  : filter === 'gestir'
                    ? 'Engir gestir skráðir'
                    : 'Enginn skráður enn'
              }
              body={
                filter === 'felagar'
                  ? 'Eftir stofnfundinn merkirðu þá sem ganga formlega í félagið.'
                  : 'Bættu við þeim sem mættu. Allir viðburðir eru opnir — flestir byrja sem gestir.'
              }
            />
          ) : (
            <>
            <label className="sort-m">
              <span className="lbl">Raða</span>
              <select
                value={`${sort.key}:${sort.dir}`}
                onChange={(e) => {
                  const [key, dir] = e.target.value.split(':') as [SortKey, Sort['dir']];
                  setSort({ key, dir });
                }}
              >
                {SORT_CHOICES.map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
            </label>
            <div className="tw">
              <table>
                <thead>
                  <tr>
                    <Th k="name" sort={sort} onSort={sortBy}>Nafn</Th>
                    <Th k="reg" sort={sort} onSort={sortBy}>Aðild</Th>
                    <Th k="attendance" sort={sort} onSort={sortBy}>Mætingar</Th>
                    <th>Athugasemd</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((m) => {
                    const editing = editId === m.id;
                    return (
                      <tr key={m.id} className={editing ? 'editing' : undefined}>
                        <td data-label="Nafn">
                          {editing ? (
                            <input
                              className="tin"
                              value={draftName}
                              onChange={(e) => setDraftName(e.target.value)}
                              onKeyDown={onKey}
                              aria-label="Nafn"
                              autoFocus
                            />
                          ) : (
                            <div className="ttl2">
                              {m.name}
                              {m.contact ? <span className="m-contact">{m.contact}</span> : null}
                            </div>
                          )}
                        </td>

                        <td data-label="Aðild">
                          {editing ? (
                            <span className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                              <button
                                type="button"
                                className={`pk${draftReg ? ' on' : ''}`}
                                aria-pressed={draftReg}
                                onClick={() => setDraftReg(true)}
                              >
                                Félagi
                              </button>
                              <button
                                type="button"
                                className={`pk${!draftReg ? ' on' : ''}`}
                                aria-pressed={!draftReg}
                                onClick={() => setDraftReg(false)}
                              >
                                Gestur
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              className={m.is_registered ? 'chip member' : 'chip guest'}
                              title={
                                m.is_registered
                                  ? `Skráður félagi${m.registered_at ? ` frá ${fmtDate(m.registered_at)}` : ''} — smelltu til að breyta í gest`
                                  : 'Gestur — smelltu til að skrá í félagið'
                              }
                              style={{ cursor: 'pointer' }}
                              onClick={() => void toggleMembership(m.id, !m.is_registered)}
                            >
                              {m.is_registered ? 'Félagi' : 'Gestur'}
                            </button>
                          )}
                        </td>

                        <td className="n" data-label="Mætingar">
                          <span className={m.attendance >= 4 ? 'chip gold' : 'chip'}>
                            {m.attendance}
                          </span>
                        </td>

                        <td data-label="Athugasemd">
                          {editing ? (
                            <span className="m-edit2">
                              <input
                                className="tin"
                                value={draftContact}
                                onChange={(e) => setDraftContact(e.target.value)}
                                onKeyDown={onKey}
                                placeholder="Netfang eða sími"
                                aria-label="Netfang eða sími"
                              />
                              <input
                                className="tin"
                                value={draftNote}
                                onChange={(e) => setDraftNote(e.target.value)}
                                onKeyDown={onKey}
                                placeholder="Athugasemd"
                                aria-label="Athugasemd"
                              />
                            </span>
                          ) : (
                            <span className="meta">{m.note || '—'}</span>
                          )}
                        </td>

                        <td className="act">
                          <span className="row-acts">
                            {editing ? (
                              <>
                                <IconButton tone="ok" label="Vista breytingar" onClick={() => void saveEdit()}>
                                  <Icon.save />
                                </IconButton>
                                <IconButton label="Hætta við" onClick={cancelEdit}>
                                  <Icon.cancel />
                                </IconButton>
                              </>
                            ) : (
                              <>
                                <IconButton label={`Breyta ${m.name}`} onClick={() => beginEdit(m)}>
                                  <Icon.pencil />
                                </IconButton>
                                <DeleteButton
                                  label={`Eyða ${m.name}`}
                                  onClick={() => void remove(m.id, m.name)}
                                />
                              </>
                            )}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </section>

        <section className="card col-4">
          <div className="card-h">
            <h2>Nýr spilari</h2>
          </div>

          <form className="form" onSubmit={submit}>
            <div className="fld">
              <label className="lbl" htmlFor="m-name">Nafn</label>
              <input
                id="m-name"
                type="text"
                placeholder="Fullt nafn"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="fld">
              <span className="lbl">Aðild</span>
              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className={`pk${!newRegistered ? ' on' : ''}`}
                  aria-pressed={!newRegistered}
                  onClick={() => setNewRegistered(false)}
                >
                  Gestur
                </button>
                <button
                  type="button"
                  className={`pk${newRegistered ? ' on' : ''}`}
                  aria-pressed={newRegistered}
                  onClick={() => setNewRegistered(true)}
                >
                  Skráður félagi
                </button>
              </div>
            </div>

            <div className="fld">
              <label className="lbl" htmlFor="m-contact">Netfang eða sími</label>
              <input
                id="m-contact"
                type="text"
                placeholder="valfrjálst — notað til að þekkja fólk aftur"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
              />
            </div>

            <div className="fld">
              <label className="lbl" htmlFor="m-note">Athugasemd</label>
              <input
                id="m-note"
                type="text"
                placeholder="Byrjandi, kom með vini, o.s.frv."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            <button className="btn" type="submit" disabled={busy || !name.trim()}>
              {busy ? 'Vista…' : 'Bæta við'}
            </button>
          </form>

          <p style={{ marginTop: 18, fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
            Allir viðburðir eru opnir, svo flestir byrja sem gestir. Smelltu á merkið í töflunni til
            að skrá einhvern í félagið — dagsetningin er stimpluð sjálfkrafa.
          </p>
          <p style={{ marginTop: 10, fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
            Hlið A telur <strong style={{ color: 'var(--ink-2)' }}>alla</strong> sem mæta, ekki bara
            félaga — það er mætingin sem segir til um hvort búðin sé raunhæf.
          </p>

          {saving && <p style={{ marginTop: 10, fontSize: 12.5, color: 'var(--gold)' }}>Vista…</p>}
        </section>
      </div>
    </div>
  );
}
